import { Button, Col, Collapse, Row, Spin, Table, Typography } from "antd";
import React from "react";
import { useSelector } from "react-redux";
import CustomTypography from "../../common/CustomTypography";
import { MinusOutlined, PlusOutlined, StarFilled } from "@ant-design/icons";
import styles from "./irregularities.module.scss";
import { CURRENCY } from "../../../utils/ConstantData";
import { CurrencyFormat, CurrencyFormatWithDecimal } from "../../../helper/CurrencyFormate";
import dayjs from 'dayjs';

const IrregularitiesPage = () => {
  const {
    irregularitiesDetails: irregularityData,
  } = useSelector(({ irregularities }) => irregularities);

  const { selectedAccountId } = useSelector(({ reportDetails }) => reportDetails);


  return (
    <Row gutter={[16, 16]}>
      <Col span={24}>
        <AccordionWithTable selectedAccountId={selectedAccountId} transactionData={irregularityData} />
      </Col>
    </Row>
  );
};

const AccordionWithTable = ({ transactionData, selectedAccountId, tableProps, ...restProps }) => {
  const [showMore, setShowMore] = React.useState({});

  const { irregularitiesLoading: loading, irregularitiesError: error } = useSelector(({ irregularities }) => irregularities);

  const handleShowMore = (index) => {
    setShowMore((prevState) => ({
      ...prevState,
      [index]: !prevState[index],
    }));
  }

  const panelStyle = {
    marginBottom: 16,
    background: "white",
    borderRadius: "6px",
    border: "1px solid #C9EEF2",
    color: "#000000E0"
  };
  const collapseItems = transactionData?.map((item, index) => {
    let dataSource, columns;

    const columnsForTransactions = [
      {
        title: "Txn Date", dataIndex: "date", key: "date", className: styles.colStyle,
        render: (value) => (
          dayjs(value).format('DD/MM/YYYY')
        ),
        sorter: (a, b) => dayjs(a.date).valueOf() - dayjs(b.date).valueOf(),
        defaultSortOrder: 'descend', // Set to 'descend' for descending
        // defaultSortOrder: 'ascend ',
      },
      {
        title: "Particulars", dataIndex: "particular", key: "particular", className: styles.colStyle
      },
      {
        title: "Counter Party", dataIndex: "counterparty", key: "counterparty", className: styles.colStyle
      },
      {
        title: `Amount (${CURRENCY})`, dataIndex: "amount", key: "amount", className: styles.colStyle, align: 'right',
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        )
      },
      {
        title: `Balance (${CURRENCY})`, dataIndex: "balance", key: "balance", className: styles.colStyle,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        )
      },
    ];

    const columnsForMonthlyTransactions = [
      {
        title: "Month", dataIndex: "month", key: "month", className: styles.colStyle,
      },
      {
        title: "Debit Count", dataIndex: "debitTransactionCount", key: "debitTransactionCount", className: styles.colStyle
      },
      {
        title: "Credit Count", dataIndex: "creditTransactionCount", key: "creditTransactionCount", className: styles.colStyle
      },
      {
        title: "Debit Amount", dataIndex: "debitAmount", key: "debitAmount", className: styles.colStyle
      },
      {
        title: "Credit Amount", dataIndex: "creditAmount", key: "creditAmount", className: styles.colStyle
      },
    ];

    const columnsForFile = [
      {
        title: "File Name", dataIndex: "fileName", key: "fileName", className: styles.colStyle
      },
      {
        title: "Authenticity", dataIndex: "authenticity", key: "authenticity", className: styles.colStyle
      },
    ]

    const salaryCreditUnchangedColumns = [
      {
        dataIndex: 'period', title: 'Period', key: 'period', className: styles.colStyle
      },
      {
        dataIndex: 'counterparty',
        title: 'Salary credit from',
        key: 'counterparty',
        className: styles.colStyle
      },
      {
        dataIndex: 'salaryDepositTransactionAmount',
        title: `Amount (${CURRENCY})`,
        key: 'salaryDepositTransactionAmount',
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      },
      {
        dataIndex: 'txnCount', title: 'Txn count', key: 'txnCount', className: styles.colStyle,
        render: value => CurrencyFormat(value, CURRENCY)
      },
    ];

    const counterPartiesBothColumns = [
      // { dataIndex: 'accountDetails', key: 'accountDetails', title: 'Bank Account', },
      {
        dataIndex: 'counterparty',
        key: 'counterparty',
        title: 'Counterparty',
        className: styles.colStyle
      },
      {
        dataIndex: 'month', key: 'month', title: 'Month', className: styles.colStyle
      },
      {
        dataIndex: 'debitAmount',
        key: 'debitAmount',
        title: `Debit amount (${CURRENCY})`,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      },
      {
        dataIndex: 'pctDebit',
        key: 'pctDebit',
        title: '% Debit amount',
        render: (value) => value + "%",
        className: styles.colStyle
      },
      {
        dataIndex: 'creditAmount',
        key: 'creditAmount',
        title: `Credit amount (${CURRENCY})`,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      },
      {
        dataIndex: 'pctCredit', key: 'pctCredit', title: '% Credit amount', className: styles.colStyle,
        render: (value) => value + "%",
      },
      {
        dataIndex: 'txnCount', key: 'txnCount', title: 'Txn count', className: styles.colStyle
      }
    ];

    const cashDepositeVSSalaryColumns = [
      {
        dataIndex: 'month', key: 'month', title: 'Month', className: styles.colStyle
      },
      {
        dataIndex: 'cashDepositTransactionCount',
        title: 'Cash txns',
        className: styles.colStyle,
        render: (value) => CurrencyFormat(value, CURRENCY)
      },
      {
        dataIndex: 'cashDepositTransactionAmount',
        key: 'cashDepositTransactionAmount',
        title: `Cash txn amount (${CURRENCY})`,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      },
      {
        dataIndex: 'salaryDepositTransactionCount',
        key: 'salaryDepositTransactionCount',
        title: 'Salary txns',
        className: styles.colStyle,
        render: (value) => CurrencyFormat(value, CURRENCY)
      },
      {
        dataIndex: 'salaryDepositTransactionAmount',
        key: 'salaryDepositTransactionAmount',
        title: `Salary amount (${CURRENCY})`,
        className: styles.colStyle,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
      },
      {
        dataIndex: 'comments', key: 'comments', title: 'Comments', className: styles.colStyle
      }
    ];

    const equalDebitAndCreditColumns = [
      {
        dataIndex: 'month', key: 'month', title: 'Month', className: styles.colStyle
      },
      {
        dataIndex: 'creditTransactionCount',
        key: 'creditTransactionCount',
        title: 'Credit txn count',
        className: styles.colStyle,
        render: (value) => CurrencyFormat(value, CURRENCY)
      },
      {
        dataIndex: 'creditAmount',
        key: 'creditAmount',
        title: `Credit amount (${CURRENCY})`,
        className: styles.colStyle,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
      },
      {
        dataIndex: 'debitTransactionCount',
        key: 'debitTransactionCount',
        title: 'Debit txn count',
        className: styles.colStyle,
        render: (value) => CurrencyFormat(value, CURRENCY)
      },
      {
        dataIndex: 'debitAmount',
        key: 'debitAmount',
        title: `Debit amount (${CURRENCY})`,
        className: styles.colStyle,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
      }
    ];

    const negativeBalanceColumns = [
      ...columnsForTransactions,
      {
        title: `Computed Balance (${CURRENCY})`,
        dataIndex: "computedBalance",
        key: "computedBalance",
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      }
    ];

    const bigDebitColumns = [
      {
        dataIndex: 'month', key: 'month', title: 'Month', className: styles.colStyle
      },
      {
        dataIndex: 'salaryDepositTransactionAmount',
        key: 'salaryDepositTransactionAmount',
        title: `Salary credit (${CURRENCY})`,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      },
      {
        dataIndex: 'debitAmount',
        key: 'debitAmount',
        title: `Debit amount (${CURRENCY})`,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      },
      {
        dataIndex: 'pctSalary',
        key: 'pctSalary',
        title: '% Salary',
        className: styles.colStyle
      },
      {
        dataIndex: 'counterparty', key: 'counterparty', title: 'Debit to', className: styles.colStyle
      },
      {
        dataIndex: 'viewTxn', key: 'viewTxn', title: '', className: styles.colStyle
      }
    ];

    const balanceMismatchColumns = [
      {
        dataIndex: 'date', key: 'date', title: 'Txn date', className: styles.colStyle
      },
      {
        dataIndex: 'particular', key: 'particular', title: 'Particulars', className: styles.colStyle
      },
      {
        dataIndex: 'counterparty', key: 'counterparty', title: 'Counterparty', className: styles.colStyle
      },
      {
        dataIndex: 'amount',
        key: 'amount',
        title: `Amount (${CURRENCY})`,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      },
      {
        dataIndex: 'balance',
        key: 'balance',
        title: `Balance (${CURRENCY})`,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      },
      {
        dataIndex: 'computedBalance',
        key: 'computedBalance',
        title: `Computed balance (${CURRENCY})`,
        render: (text, record) => (
          <span style={{ color: record.computedBalance > 0 ? "" : "red" }}>
            {text}
          </span>
        ),
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        onHeaderCell: (column) => {
          return {
            className: styles.colHead // TODO: Add class to header cell
          };
        },
        className: styles.colStyle
      },
      {
        dataIndex: 'balanceGap',
        key: 'balanceGap',
        title: `Balance gap (${CURRENCY})`,
        render: (value) => (
          <Typography.Text {...(value < 0 ? { type: "danger" } : {})}>{CurrencyFormatWithDecimal(value, null, CURRENCY)}</Typography.Text>
        ),
        className: styles.colStyle
      }
    ];


    if (selectedAccountId === "all") {
      columnsForTransactions.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
      columnsForMonthlyTransactions.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
      columnsForFile.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
      salaryCreditUnchangedColumns.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
      counterPartiesBothColumns.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
      cashDepositeVSSalaryColumns.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
      equalDebitAndCreditColumns.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
      negativeBalanceColumns.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
      bigDebitColumns.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
      balanceMismatchColumns.unshift({
        title: "Bank Account",
        dataIndex: "accountDetails",
        key: "accountDetails",
        render: value => (
          <>
            <StarFilled style={{ color: '#1890ff', marginRight: 8, marginTop: -11, fontSize: '20px' }} />
            {`${value?.bankAbbreviation} - ${value?.accountNumber.slice(8, 12)} - ${value?.accountType}`}
          </>
        )
      })
    }





    if (item.activity === "Salary Credit Amount remains unchanged over extended period") {
      dataSource = item.monthlyTransactions;
      columns = salaryCreditUnchangedColumns;
    } else if (item.activity === "Parties present in both debits and credits") {
      dataSource = item.monthlyTransactions;
      columns = counterPartiesBothColumns;
    } else if (item.activity === "More cash deposits vs salary") {
      dataSource = item.monthlyTransactions;
      columns = cashDepositeVSSalaryColumns;
    } else if (item.activity === "Equal debits & credits") {
      dataSource = item.monthlyTransactions;
      columns = equalDebitAndCreditColumns;
    } else if (item.activity === "Negative Balance") {
      dataSource = item.transactions;
      columns = negativeBalanceColumns;
    } else if (item.activity === "Immediate big debit after Salary credit") {
      dataSource = item.monthlyTransactions;
      columns = bigDebitColumns;
    } else if (item.activity === "Balance Mismatch") {
      dataSource = item.transactions;
      columns = balanceMismatchColumns;
    }

    else if (item.transactions) {
      dataSource = item.transactions;
      columns = columnsForTransactions;
    } else if (item.monthlyTransactions) {
      dataSource = item.monthlyTransactions;
      columns = columnsForMonthlyTransactions;
    } else if (item.files) {
      dataSource = item.files;
      columns = columnsForFile;
    }


    const getActivity = activity => {
      if (
        activity.toLowerCase() === 'ATM withdrawals above 20,000'.toLowerCase()
      ) {
        return `ATM withdrawals above ${CURRENCY}20,000`;
      } else if (
        activity.toLowerCase() === 'RTGS Payments below 2,00,000'.toLowerCase()
      ) {
        return `RTGS Payments below ${CURRENCY}2 lakhs`;
      } else if (
        activity.toLowerCase() === 'RTGS Payments below 2,00,000'.toLowerCase()
      ) {
        return `RTGS Payments below ${CURRENCY}2 lakhs`;
      } else if (activity.toLowerCase() === 'Balance Mismatch'.toLowerCase()) {
        return 'Balance vs Computed balance mismatch';
      } else if (activity.toLowerCase() === 'Negative balance'.toLowerCase()) {
        return 'Negative computed balance in DR Txns';
      } else return activity;
    };

    const getRowItemNumber = (data) => {
      return data?.length;
    };

    return {
      key: index.toString(),
      label: <CustomTypography style={{ fontSize: "16px", fontWeight: 500, lineHeight: "24px", color: "#000000E0", fontFamily: "Satoshi" }}>{`${getActivity(item.activity)} (${getRowItemNumber(dataSource)})`}</CustomTypography>,
      children: <>
        <Table loading={loading}
          dataSource={dataSource?.length >= 10 ? (!showMore[index] ? dataSource.slice(0, 10) : dataSource) : dataSource}
          columns={columns}
          pagination={false}
        />
        {
          dataSource?.length >= 10 && (
            <div style={{ display: "flex", justifyContent: "flex-end", alignItems: "center", marginTop: "16px" }}>
              <Button onClick={() => handleShowMore(index)}>
                {showMore[index] ? "Show Less" : "Show All"}
              </Button>
            </div>
          )
        }
      </>,
      style: panelStyle,
    };
  });

  const IconWrapper = ({ children }) => {
    return (
      <div style={{ height: "16px", width: "16px", borderRadius: "4px", border: "1px solid #F0F0F0", display: "flex", justifyContent: "center" }}>
        {children}
      </div>
    )
  }

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px" }}>
        <Spin size="large" />
      </div>
    )
  }

  if (error) {
    return (
      <div style={{ display: "flex", justifyContent: "center", alignItems: "center", height: "300px", flexDirection: "column" }}>
        <CustomTypography style={{ fontSize: "16px", fontWeight: 500, lineHeight: "24px", color: "#000000E0", fontFamily: "Satoshi" }}>Error fetching data</CustomTypography>
        <CustomTypography type={"paragraph"} style={{ fontSize: "10px", fontWeight: 300, lineHeight: "16px", color: "#000000E0", fontFamily: "Satoshi" }}>{error}</CustomTypography>
      </div>
    )
  }

  return (
    <Collapse
      bordered={false}
      // defaultActiveKey={['0']}
      style={{
        background: "#F7F7FC",
      }}
      // accordion
      items={collapseItems}
      expandIcon={({ isActive }) => <IconWrapper>{isActive ? <MinusOutlined /> : <PlusOutlined />}</IconWrapper>}
      {...restProps}
    />
  )
};

export default IrregularitiesPage;
